import { cn } from "@acme/utils";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const statusPillVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full py-[3px] pr-2.5 pl-2 text-xs font-medium leading-[1.4] tracking-[0.01em]",
  {
    variants: {
      status: {
        pending:
          "bg-[var(--status-pending-bg)] text-[var(--status-pending-fg)]",
        done: "bg-[var(--status-done-bg)] text-[var(--status-done-fg)]",
        empty: "bg-[var(--status-empty-bg)] text-[var(--status-empty-fg)]",
        rejected:
          "bg-[var(--status-rejected-bg)] text-[var(--status-rejected-fg)]",
      },
    },
    defaultVariants: {
      status: "empty",
    },
  },
);

type StatusPillStatus = NonNullable<
  VariantProps<typeof statusPillVariants>["status"]
>;

const defaultLabels: Record<StatusPillStatus, string> = {
  pending: "Pending",
  done: "Done",
  empty: "Empty",
  rejected: "Rejected",
};

export interface StatusPillProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof statusPillVariants> {
  status: StatusPillStatus;
}

function StatusPill({
  status,
  className,
  children,
  ...props
}: StatusPillProps) {
  return (
    <span className={cn(statusPillVariants({ status }), className)} {...props}>
      <span
        aria-hidden="true"
        className="size-1.5 rounded-full bg-current"
      />
      {children ?? defaultLabels[status]}
    </span>
  );
}

export { StatusPill, statusPillVariants };
export type { StatusPillStatus };
